import "@src/shared/utils/transcribe/mediaRecorder.init";

import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useGetEncounterById } from "@elation/api-client/src/stitch-web";

import {
  AuthenticatedAppHeader,
  AuthenticatedHeaderStepperV2,
} from "@src/features/auth/v2";
import { useAppBusEvent } from "@src/features/sse";
import { Button } from "@src/shared/components/ui/button";
import { navigationV2 } from "@src/shared/navigation/v2";
import { parsePositiveIntParam } from "@src/shared/utils/params";

import {
  noteGenerationPollIntervalMs,
  resolveVisitBackTarget,
  useEncounter,
  useEncounterIdFromParams,
  useVisitRecordingSession,
  type VisitLocationState,
} from "../../../application";
import { EncounterEntity, markEncounterNoteReady } from "../../../domain";
import { VisitPageV2 } from "../../../ui/pages/v2";

const VISIT_STEPS = [
  { id: "agenda", label: "Agenda" },
  { id: "visit", label: "Visit" },
  { id: "note", label: "Note" },
];

export function VisitV2Route() {
  const encounterId = useEncounterIdFromParams();
  const location = useLocation();
  const isCreateMode = encounterId == null;
  const [isPatientSelectorOpen, setIsPatientSelectorOpen] =
    useState(isCreateMode);
  const visitState = location.state as VisitLocationState | null;

  const {
    encounterId: actorEncounterId,
    completedAtBaseline,
    isEncounterNoteGenerationActive,
  } = useEncounter();

  const isNoteGenerationActiveForRoute =
    encounterId != null &&
    actorEncounterId === encounterId &&
    isEncounterNoteGenerationActive;

  const encounter = useGetEncounterById(encounterId ?? 0, {
    query: {
      enabled: encounterId != null,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 30_000),
      refetchInterval: (query) =>
        noteGenerationPollIntervalMs(query.state.data?.data, {
          localOrActorActive: isNoteGenerationActiveForRoute,
          hasQueryError: query.state.error != null,
          completionBaseline: completedAtBaseline,
        }),
    },
  });
  const encounterData = encounter.data?.data;
  const patientId = encounterData?.patientId;

  const isGeneratingNote =
    isNoteGenerationActiveForRoute ||
    EncounterEntity.isAwaitingGeneratedNote(encounterData);

  useEffect(() => {
    if (
      encounterId == null ||
      !isNoteGenerationActiveForRoute ||
      !EncounterEntity.hasGeneratedNoteForFinish(
        encounterData,
        completedAtBaseline,
      )
    ) {
      return;
    }

    markEncounterNoteReady(encounterId);
  }, [
    completedAtBaseline,
    encounterData,
    encounterId,
    isNoteGenerationActiveForRoute,
  ]);

  const {
    recording,
    recordingStep,
    scribingSessionId,
    activeEncounterId,
    startVisitRecording,
    resetScribeForEncounter,
  } = useVisitRecordingSession({
    encounterId,
    patientId,
  });

  const backTo = resolveVisitBackTarget({
    state: visitState,
    fallbackPath: navigationV2.visits(),
  });

  const noteReadyForDisplay = EncounterEntity.isNoteReadyForDisplay({
    encounter: encounterData,
    isGeneratingNote,
    completionBaseline: completedAtBaseline,
  });

  const handlePatientSelectorOpenChange = useCallback((open: boolean) => {
    setIsPatientSelectorOpen(open);
  }, []);

  useEffect(() => {
    if (isCreateMode) setIsPatientSelectorOpen(true);
  }, [isCreateMode]);

  useAppBusEvent("client.router_params_changed", ({ params }) => {
    const nextEncounterId = parsePositiveIntParam(params.encounterId);

    if (nextEncounterId !== encounterId) return;
    if (nextEncounterId === activeEncounterId) return;

    resetScribeForEncounter(nextEncounterId);
  });

  useAppBusEvent("client.router_search_params_changed", ({ searchParams }) => {
    const parsedEncounterId = parsePositiveIntParam(
      searchParams.get("encounterId"),
    );

    if (parsedEncounterId !== encounterId) return;
    if (parsedEncounterId === activeEncounterId) return;

    resetScribeForEncounter(parsedEncounterId);
  });

  return (
    <>
      {noteReadyForDisplay ? null : (
        <AuthenticatedAppHeader>
          <Button
            type="button"
            asChild
            variant="ghost"
            className="h-8 px-2 text-[12.5px] font-medium"
          >
            <Link to={backTo} replace>
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back
            </Link>
          </Button>
          <div className="flex flex-1 justify-center">
            <AuthenticatedHeaderStepperV2
              steps={VISIT_STEPS}
              currentStepId={EncounterEntity.stage({
                encounter: encounterData,
                recordingStep,
                isEncounterFinalizing: isGeneratingNote,
              })}
            />
          </div>
          <div className="w-[58px]" aria-hidden="true" />
        </AuthenticatedAppHeader>
      )}
      <VisitPageV2
        encounterId={encounterId}
        patientId={patientId}
        isPatientSelectorOpen={isPatientSelectorOpen}
        isLoadingEncounter={!isCreateMode && encounter.isLoading}
        isEncounterError={!isCreateMode && encounter.isError}
        isGeneratingNote={isGeneratingNote}
        completionBaseline={completedAtBaseline}
        encounterError={encounter.error}
        recording={recording}
        scribingSessionId={scribingSessionId}
        backTo={noteReadyForDisplay ? backTo : undefined}
        onEncounterRetry={() => {
          void encounter.refetch();
        }}
        onPatientSelectorOpenChange={handlePatientSelectorOpenChange}
        onStartVisit={() => void startVisitRecording()}
      />
    </>
  );
}
